/**
 * Branch business-day ("día comercial") helpers for the dashboard widgets
 * and the business-day note. Pure: no Vue, no stores.
 *
 * A branch with start hour 8 runs its business day 24/09 from 24/09 08:00
 * to 25/09 07:59 (branch time zone); hourly views draw hours in that order
 * (8 … 23, 0 … 7) and keep the real clock hour as the label.
 */
import { IStatsContext } from '@/interfaces/IStatsContext';
import { parseLocalDayInput } from '@/domain/pos/history-date-presets';

/** Business "today" the dashboard resolves once per load/branch switch. */
export interface IBusinessDayAnchor {
  /** Restaurant this anchor was resolved for; every stats call uses it. */
  restaurantId: number;
  /** Branch this anchor was resolved for; every stats call uses it. */
  branchId: number;
  /** Current business date as a local-midnight Date (calendar-day carrier). */
  today: Date;
  /** Start hour 0-23 (0 = calendar days). */
  cutoffHour: number;
  /** Branch IANA zone; null when the context could not be loaded. */
  timeZone: string | null;
  /** Raw context; null when GET /stats/context failed or was unusable. */
  context: IStatsContext | null;
}

/** Start hour when it is an integer 0-23, otherwise 0 (calendar days). */
export function normalizeCutoffHour(value: unknown): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 23
    ? value
    : 0;
}

/**
 * Anchors "today" on the branch business date. Without a usable context the
 * browser's local day and calendar hours are used (legacy behavior).
 */
export function resolveBusinessDayAnchor(
  restaurantId: number,
  branchId: number,
  context: IStatsContext | null,
  now: Date = new Date()
): IBusinessDayAnchor {
  const today = context
    ? parseLocalDayInput(context.currentBusinessDate)
    : null;
  if (context && today) {
    return {
      restaurantId,
      branchId,
      today,
      cutoffHour: normalizeCutoffHour(context.businessDayStartHour),
      timeZone: context.timeZone || null,
      context,
    };
  }
  return {
    restaurantId,
    branchId,
    today: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    cutoffHour: 0,
    timeZone: null,
    context: null,
  };
}

/** Position of a clock hour on the business-day axis (0 = start hour). */
export function businessHourPosition(hour: number, cutoffHour: number): number {
  return (((hour - cutoffHour) % 24) + 24) % 24;
}

/** The 24 clock hours in business-day order (start hour first). */
export function businessHourOrder(cutoffHour: number): number[] {
  return Array.from(
    { length: 24 },
    (_, position) => (position + cutoffHour) % 24
  );
}

/**
 * Calendar weekday (0-6, Mon-Sun) of a clock hour inside a business weekday:
 * hours before the start hour happen after midnight, on the next calendar
 * day (Monday's business day at 01:00 is Tuesday 01:00).
 */
export function calendarWeekdayIndex(
  businessWeekday: number,
  hour: number,
  cutoffHour: number
): number {
  return cutoffHour > 0 && hour < cutoffHour
    ? (businessWeekday + 1) % 7
    : businessWeekday;
}

/**
 * Current wall-clock hour (0-23) in the branch zone; the browser's hour when
 * the zone is unknown or invalid. No `hourCycle` option: unsupported on the
 * older Chrome builds some POS devices still run.
 */
export function currentHourInTimeZone(
  timeZone: string | null,
  now: Date = new Date()
): number {
  if (timeZone) {
    try {
      const hour = Number(
        new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          hour12: false,
          timeZone,
        }).format(now)
      );
      // Some engines print midnight as "24".
      if (Number.isInteger(hour)) return hour % 24;
    } catch {
      // Invalid zone (RangeError): fall back to the browser's hour.
    }
  }
  return now.getHours();
}

/**
 * How many hourly buckets the "Hoy" chart shows: up to the current hour, or
 * the whole day once the anchored business day is over (a dashboard left
 * open past the start hour keeps its snapshot but is never cut short).
 */
export function getTodayBucketCount(
  anchor: IBusinessDayAnchor,
  now: Date = new Date()
): number {
  const dayOver = anchor.context
    ? now.getTime() >= Date.parse(anchor.context.window.to)
    : anchor.today.getFullYear() !== now.getFullYear() ||
      anchor.today.getMonth() !== now.getMonth() ||
      anchor.today.getDate() !== now.getDate();
  if (dayOver) return 24;
  return (
    businessHourPosition(
      currentHourInTimeZone(anchor.timeZone, now),
      anchor.cutoffHour
    ) + 1
  );
}

/** The business-day note only exists when the day does not start at 00:00. */
export function hasBusinessDayCutoff(context: IStatsContext): boolean {
  return context.businessDayStartHour > 0;
}

/**
 * Params of `v2Stats.range.businessFootnote`:
 * "Día comercial 21/08 = 21/08 06:00 → 22/08 05:59".
 */
export function getBusinessFootnoteParams(
  context: IStatsContext,
  locale?: string
): { date: string; from: string; to: string } | null {
  const day = parseLocalDayInput(context.currentBusinessDate);
  if (!day) return null;
  const next = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  const fmt = (d: Date) =>
    d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
  const hour = String(context.businessDayStartHour).padStart(2, '0');
  const endHour = String((context.businessDayStartHour + 23) % 24).padStart(
    2,
    '0'
  );
  return {
    date: fmt(day),
    from: `${fmt(day)} ${hour}:00`,
    to: `${fmt(next)} ${endHour}:59`,
  };
}

export interface IBusinessDaySettingsLinkInput {
  /** restaurant.stringId */
  restaurantSlug: string;
  branchId: number;
  /** Active branches the user can access (restaurantStore.visibleBranches). */
  activeBranchCount: number;
  effectiveTier: number;
  canEditStoreSettings: boolean;
  canEditBranchSettings: boolean;
}

/**
 * Where the business-day start hour can be edited by this user, or null.
 * Mirrors RestaurantSettings.multipleBranches and the /settings and
 * /branches/:branchId route guards; keep in sync.
 */
export function getBusinessDaySettingsPath(
  input: IBusinessDaySettingsLinkInput
): string | null {
  const {
    restaurantSlug,
    branchId,
    activeBranchCount,
    effectiveTier,
    canEditStoreSettings,
    canEditBranchSettings,
  } = input;
  if (!restaurantSlug) return null;

  const multiBranch = effectiveTier > 1 && activeBranchCount > 1;
  if (multiBranch || (effectiveTier > 1 && !canEditStoreSettings)) {
    return canEditBranchSettings
      ? `/${restaurantSlug}/branches/${branchId}?tab=orders`
      : null;
  }
  return effectiveTier > 0 && canEditStoreSettings && canEditBranchSettings
    ? `/${restaurantSlug}/settings?tab=orders`
    : null;
}
